import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";

import { DatePicker } from "@/components/date-picker";
import { TimePicker } from "@/components/time-picker";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useSaveTimeEntry } from "@/features/presence/hooks/use-save-time-entry";
import type { TimeEntry } from "@/features/presence/types";
import {
	isoToTime,
	timeEntryErrorMessage,
	timeToISO,
	workDateLabel,
} from "@/features/presence/utils/format";
import { currentWorkDay, dateOnlyToDate, dateToDateOnly } from "@/lib/format";
import {
	type TimeEntryFormValues,
	timeEntrySchema,
} from "../../schemas/time-entry-schema";
import type { Worker } from "../../types";

export interface TimeEntryDefaults {
	workerId: string;
	workerName: string;
	workDate: string;
}

export function TimeEntryDialog({
	open,
	onOpenChange,
	mode,
	entry,
	workers,
	defaults,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	mode: "create" | "edit";
	entry?: TimeEntry;
	workers: Worker[];
	defaults?: TimeEntryDefaults;
}) {
	const saveEntry = useSaveTimeEntry();
	const locked = mode === "create" && defaults != null;
	const defaultWorkerId = defaults?.workerId;
	const defaultWorkDate = defaults?.workDate;

	const {
		register,
		handleSubmit,
		control,
		reset,
		formState: { errors },
	} = useForm<TimeEntryFormValues>({
		resolver: zodResolver(timeEntrySchema),
		defaultValues: {
			mode,
			workerId: "",
			workDate: undefined,
			clockedInAt: "",
			clockedOutAt: "",
			editNote: "",
		},
	});

	useEffect(() => {
		if (!open) return;
		reset({
			mode,
			workerId: entry?.worker.id ?? defaultWorkerId ?? "",
			workDate: dateOnlyToDate(defaultWorkDate),
			clockedInAt: isoToTime(entry?.clockedInAt),
			clockedOutAt: isoToTime(entry?.clockedOutAt),
			editNote: "",
		});
	}, [open, mode, entry, defaultWorkerId, defaultWorkDate, reset]);

	async function onSubmit(values: TimeEntryFormValues) {
		const workDate =
			mode === "edit"
				? (entry?.workDate ?? "")
				: dateToDateOnly(values.workDate);
		const clockedInAt = timeToISO(workDate, values.clockedInAt);
		if (!clockedInAt) {
			toast.error("Informe um horário de entrada válido.");
			return;
		}
		const clockedOutAt = values.clockedOutAt
			? timeToISO(workDate, values.clockedOutAt)
			: undefined;

		try {
			if (mode === "edit" && entry) {
				await saveEntry.mutateAsync({
					entryId: entry.id,
					clockedInAt,
					clockedOutAt,
					editNote: values.editNote,
				});
				toast.success("Registro corrigido");
			} else {
				await saveEntry.mutateAsync({
					workerId: values.workerId,
					workDate,
					clockedInAt,
					clockedOutAt,
					editNote: values.editNote,
				});
				toast.success("Registro criado");
			}
			onOpenChange(false);
		} catch (err) {
			toast.error(
				timeEntryErrorMessage(err, "Não foi possível salvar o registro."),
			);
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="time-entry-dialog">
				<DialogHeader>
					<DialogTitle>
						{mode === "edit"
							? "Corrigir ponto"
							: locked
								? "Adicionar intervalo"
								: "Registrar dia"}
					</DialogTitle>
					<DialogDescription>
						{mode === "edit" && entry
							? `${entry.worker.name} · ${workDateLabel(entry.workDate)}`
							: locked && defaults
								? `${defaults.workerName} · ${workDateLabel(defaults.workDate)}`
								: "Registre manualmente um dia que ficou sem ponto."}
					</DialogDescription>
				</DialogHeader>

				<form
					id="time-entry-form"
					className="time-entry-dialog__form"
					onSubmit={handleSubmit(onSubmit)}
					noValidate
				>
					{mode === "create" && !locked && (
						<>
							<div className="form-group">
								<Label htmlFor="time-entry-worker">Pessoa</Label>
								<Controller
									control={control}
									name="workerId"
									render={({ field }) => (
										<Select value={field.value} onValueChange={field.onChange}>
											<SelectTrigger
												id="time-entry-worker"
												className="time-entry-dialog__select"
												aria-invalid={!!errors.workerId}
											>
												<SelectValue placeholder="Selecione uma pessoa" />
											</SelectTrigger>
											<SelectContent>
												{workers.map((worker) => (
													<SelectItem key={worker.id} value={worker.id}>
														{worker.name}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									)}
								/>
								{errors.workerId && (
									<p className="form-error">{errors.workerId.message}</p>
								)}
							</div>

							<div className="form-group">
								<Label htmlFor="time-entry-date">Dia</Label>
								<Controller
									control={control}
									name="workDate"
									render={({ field }) => (
										<DatePicker
											id="time-entry-date"
											modal
											selected={field.value}
											onSelected={field.onChange}
											aria-invalid={!!errors.workDate}
											calendarProps={{ disabled: { after: currentWorkDay() } }}
										/>
									)}
								/>
								{errors.workDate && (
									<p className="form-error">{errors.workDate.message}</p>
								)}
							</div>
						</>
					)}

					<div className="time-entry-dialog__times">
						<div className="form-group">
							<Label htmlFor="time-entry-in">Entrada</Label>
							<Controller
								control={control}
								name="clockedInAt"
								render={({ field }) => (
									<TimePicker
										id="time-entry-in"
										modal
										placeholder="07:00"
										value={field.value}
										onChange={field.onChange}
										onBlur={field.onBlur}
										aria-invalid={!!errors.clockedInAt}
									/>
								)}
							/>
							{errors.clockedInAt && (
								<p className="form-error">{errors.clockedInAt.message}</p>
							)}
						</div>

						<div className="form-group">
							<Label htmlFor="time-entry-out">Saída</Label>
							<Controller
								control={control}
								name="clockedOutAt"
								render={({ field }) => (
									<TimePicker
										id="time-entry-out"
										modal
										placeholder="17:00"
										value={field.value}
										onChange={field.onChange}
										onBlur={field.onBlur}
										aria-invalid={!!errors.clockedOutAt}
									/>
								)}
							/>
							{errors.clockedOutAt && (
								<p className="form-error">{errors.clockedOutAt.message}</p>
							)}
						</div>
					</div>

					<div className="form-group">
						<Label htmlFor="time-entry-note">Motivo do ajuste</Label>
						<Textarea
							id="time-entry-note"
							placeholder="Ex.: esqueceu de bater o ponto na chegada"
							aria-invalid={!!errors.editNote}
							{...register("editNote")}
						/>
						{errors.editNote && (
							<p className="form-error">{errors.editNote.message}</p>
						)}
					</div>
				</form>

				<DialogFooter>
					<Button
						type="button"
						variant="ghost"
						onClick={() => onOpenChange(false)}
						disabled={saveEntry.isPending}
					>
						Cancelar
					</Button>
					<Button
						type="submit"
						form="time-entry-form"
						disabled={saveEntry.isPending}
					>
						{saveEntry.isPending && (
							<Loader2 className="time-entry-dialog__spinner" />
						)}
						Salvar
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
